import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Badge,
  Collapse,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  AccountCircle,
  ExpandLess,
  ExpandMore,
  FiberManualRecord,
  Business as TenantIcon,
  CardMembership as SubscriptionIcon,
  Receipt as BillingIcon,
  SupportAgent as SupportIcon,
  Flag as FeatureFlagIcon,
  AdminPanelSettings as RoleTemplateIcon,
  Analytics as AnalyticsIcon,
  Notifications as NotificationsIcon,
  Policy as AuditIcon,
  Security as SecurityIcon,
  DataUsage as UsageIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { notificationApi } from '../../../api/notificationApi';
import { logout } from '../../../store/slices/authSlice';
import { endSession } from '../../../utils/endSession';
import { AppDispatch, RootState } from '../../../store/store';
import HelpMenu from '../../../components/Help/HelpMenu';

const drawerWidth = 270;

interface MenuItemType {
  text: string;
  icon?: React.ReactNode;
  path?: string;
  children?: MenuItemType[];
}

const isChildActive = (item: MenuItemType, pathname: string): boolean => {
  if (item.path && pathname.startsWith(item.path)) return true;
  if (item.children) return item.children.some((child) => isChildActive(child, pathname));
  return false;
};

const getLevelStyles = (level: number, isActive: boolean) => {
  const base = {
    py: 0.4,
    minHeight: 36,
    borderRadius: 1.5,
    mx: 0.5,
    mb: 0.3,
    transition: 'all 0.15s ease',
  };
  if (level === 0) {
    return {
      ...base,
      py: 0.6,
      minHeight: 42,
      mx: 1,
      ...(isActive && {
        bgcolor: 'primary.main',
        color: '#fff',
        '& .MuiListItemIcon-root': { color: '#fff' },
        '&:hover': { bgcolor: 'primary.dark' },
      }),
    };
  }
  if (level === 1) {
    return {
      ...base,
      pl: 2.5,
      fontSize: '0.875rem',
      ...(isActive && {
        bgcolor: 'rgba(102, 126, 234, 0.12)',
        color: 'primary.main',
        fontWeight: 600,
        borderLeft: '3px solid',
        borderColor: 'primary.main',
        borderRadius: '0 8px 8px 0',
        '& .MuiListItemIcon-root': { color: 'primary.main' },
      }),
    };
  }
  return {
    ...base,
    pl: 2 + level * 1.5,
    ...(isActive && {
      bgcolor: 'rgba(102, 126, 234, 0.06)',
      color: 'primary.main',
    }),
  };
};

interface SidebarMenuItemProps {
  item: MenuItemType;
  level: number;
  pathname: string;
  navigate: (path: string) => void;
}

const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({ item, level, pathname, navigate }) => {
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const childActive = isChildActive(item, pathname);
  const selfActive = item.path
    ? pathname === item.path || (item.path !== '/super-admin/dashboard' && pathname.startsWith(item.path + '/'))
    : false;
  const isActive = selfActive || (hasChildren && childActive);
  const [open, setOpen] = useState(childActive);

  const handleClick = useCallback(() => {
    if (hasChildren) {
      setOpen((prev) => !prev);
    } else if (item.path) {
      navigate(item.path);
    }
  }, [hasChildren, item.path, navigate]);

  const expandIcon = hasChildren ? (
    open ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />
  ) : null;

  const leafIcon =
    !hasChildren && level >= 2 ? (
      <FiberManualRecord sx={{ fontSize: 6, color: selfActive ? 'primary.main' : 'text.disabled' }} />
    ) : null;

  const textStyle = useMemo(
    () => ({
      '& .MuiTypography-root': {
        fontSize: level === 0 ? '0.9rem' : level === 1 ? '0.84rem' : '0.8rem',
        fontWeight: isActive ? 600 : level === 0 ? 500 : 400,
        color: selfActive && level === 0 ? '#fff' : selfActive ? 'primary.main' : hasChildren && childActive ? 'primary.main' : 'text.primary',
        lineHeight: 1.4,
      },
    }),
    [level, isActive, selfActive, hasChildren, childActive]
  );

  return (
    <>
      <ListItem disablePadding>
        {/* <ListItemButton
          onClick={handleClick}
          sx={{
            ...getLevelStyles(level, selfActive),
            '&:hover': { bgcolor: selfActive ? undefined : 'rgba(102, 126, 234, 0.06)' },
          }}
        > */}
        <ListItemButton
            onClick={handleClick}
            sx={{
              ...getLevelStyles(level, selfActive),
              '&:hover': {
                bgcolor: selfActive
                  ? 'primary.dark'
                  : 'rgba(102, 126, 234, 0.12)',
                '& .MuiTypography-root': {
                  color: selfActive ? '#fff' : 'primary.main',
                },
                '& .MuiListItemIcon-root': {
                  color: selfActive ? '#fff' : 'primary.main',
                },
              },
            }}
          >
          {item.icon && (
            <ListItemIcon sx={{ minWidth: 34, color: isActive ? 'primary.main' : 'text.secondary' }}>
              {item.icon}
            </ListItemIcon>
          )}
          {leafIcon && <ListItemIcon sx={{ minWidth: 20 }}>{leafIcon}</ListItemIcon>}
          <ListItemText primary={item.text} sx={textStyle} />
          {expandIcon}
        </ListItemButton>
      </ListItem>
      {hasChildren && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List disablePadding>
            {item.children!.map((child) => (
              <SidebarMenuItem key={child.text} item={child} level={level + 1} pathname={pathname} navigate={navigate} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const menuItems: MenuItemType[] = [
  { text: 'Dashboard', icon: <DashboardIcon fontSize="small" />, path: '/super-admin/dashboard' },
  { text: 'Tenant Management', icon: <TenantIcon fontSize="small" />, path: '/super-admin/tenants' },
  { text: 'Subscription Plans', icon: <SubscriptionIcon fontSize="small" />, path: '/super-admin/subscription-plans' },
  { text: 'Billing & Invoices', icon: <BillingIcon fontSize="small" />, path: '/super-admin/billing' },
  { text: 'Support Tickets', icon: <SupportIcon fontSize="small" />, path: '/super-admin/support-tickets' },
  { text: 'Feature Flags', icon: <FeatureFlagIcon fontSize="small" />, path: '/super-admin/feature-flags' },
  { text: 'Role Templates', icon: <RoleTemplateIcon fontSize="small" />, path: '/super-admin/role-templates' },
  { text: 'Platform Analytics', icon: <AnalyticsIcon fontSize="small" />, path: '/super-admin/analytics' },
  { text: 'Notifications', icon: <NotificationsIcon fontSize="small" />, path: '/super-admin/notifications' },
  { text: 'Audit Logs', icon: <AuditIcon fontSize="small" />, path: '/super-admin/audit-logs' },
  { text: 'Security Monitoring', icon: <SecurityIcon fontSize="small" />, path: '/super-admin/security' },
  { text: 'Usage Monitoring', icon: <UsageIcon fontSize="small" />, path: '/super-admin/usage' },
  { text: 'System Settings', icon: <SettingsIcon fontSize="small" />, path: '/super-admin/settings' },
];

const SuperAdminLayout: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);

  const handleDrawerToggle = () => setOpen(!open);
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleNavigate = useCallback((path: string) => {
    navigate(path);
    if (isMobile) setOpen(false);
  }, [navigate, isMobile]);

  const handleLogout = async () => {
    await endSession();
    dispatch(logout());
    navigate('/login');
  };

  const loadUnreadNotificationCount = useCallback(async () => {
    try {
      const response = await notificationApi.getMyNotifications({ pageNumber: 1, pageSize: 1 });
      setUnreadNotificationCount(response.unreadCount ?? 0);
    } catch {
      setUnreadNotificationCount(0);
    }
  }, []);

  React.useEffect(() => {
    if (!user) {
      setUnreadNotificationCount(0);
      return;
    }

    void loadUnreadNotificationCount();
    const intervalId = window.setInterval(() => {
      void loadUnreadNotificationCount();
    }, 30000);

    return () => window.clearInterval(intervalId);
  }, [user, loadUnreadNotificationCount]);

  const drawerContent = (
    <>
      <Toolbar />
      <Box
        sx={{
          overflow: 'auto',
          mt: 1,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 4 },
        }}
      >
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Avatar sx={{ width: 56, height: 56, mx: 'auto', mb: 1, bgcolor: 'secondary.main' }}>
            {user?.fullName?.charAt(0)?.toUpperCase() || 'S'}
          </Avatar>
          <Typography variant="subtitle2" fontWeight="bold">{user?.fullName}</Typography>
          <Typography variant="caption" color="text.secondary">Super Admin</Typography>
        </Box>
        <Divider sx={{ mb: 1 }} />
        <List disablePadding sx={{ px: 0.5 }}>
          {menuItems.map((item) => (
            <SidebarMenuItem key={item.text} item={item} level={0} pathname={location.pathname} navigate={handleNavigate} />
          ))}
        </List>
      </Box>
    </>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Toolbar>
          <IconButton color="inherit" onClick={handleDrawerToggle} edge="start" sx={{ mr: { xs: 1, sm: 2 } }}>
            {open && !isMobile ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Evita HRMS — Super Admin
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpMenu />
            <IconButton color="inherit" onClick={() => navigate('/super-admin/notifications')}>
              <Badge badgeContent={unreadNotificationCount} color="error" max={99}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>{user?.fullName}</Typography>
            <IconButton color="inherit" onClick={handleMenuOpen}>
              <AccountCircle />
            </IconButton>
          </Box>
          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => navigate('/')}>
              <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
              Tenant Dashboard
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile: temporary overlay drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          anchor="left"
          open={open}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', borderRight: '1px solid', borderColor: 'divider' },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="persistent"
          anchor="left"
          open={open}
          sx={{
            width: open ? drawerWidth : 0,
            flexShrink: 0,
            transition: 'width 0.3s',
            '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', borderRight: '1px solid', borderColor: 'divider' },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2, md: 3 },
          width: !isMobile && open ? `calc(100% - ${drawerWidth}px)` : '100%',
          transition: 'width 0.3s, margin 0.3s',
          ml: 0,
          minWidth: 0,
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default SuperAdminLayout;
